// `server.draining` (spec/09-realtime-protocol.md §6.5, §8; spec/21 §5; issue #41 rule 4).
//
// The frame a gateway node sends to every socket it holds before it stops: "I am going away, come
// back in `reconnectAfterMs`". It exists for one reason, and it is a number in spec/19 §7 rule 9
// rather than a nicety — 55,000 stations whose node disappeared reconnect at once, and the TLS
// handshakes, not the sockets, are what the load balancer cannot absorb. Each socket is given a
// **different** delay, spread over the drain window, so the fleet comes back over half a minute
// instead of in one second.
//
// Three fields, and each one is load-bearing:
//
//   reconnectAfterMs  what this client should wait before reconnecting. Different for every
//                     socket on the node; `planDrain` in apps/server/src/entrypoints/drain.ts is
//                     what spreads them.
//   closesInMs        when this node will close the socket regardless. A client that is mid-frame
//                     knows how long it has; a client that ignores the frame is not surprised.
//   reason            why. A deploy is expected and a client should reconnect quietly; anything
//                     else may be worth a line in the station's log.
//
// The client's own backoff is still its own (spec/09 §8: full jitter, 0–5 s first attempt for
// stations, cap 60 s). `reconnectAfterMs` is added to it, not instead of it: a node that dies
// without warning sends no frame at all, so the jitter has to be there anyway.
import { z } from "../zod.js";
import { SERVER_DRAINING } from "./constants.js";
import { realtimeMessage } from "./envelope.js";
export { SERVER_DRAINING };
/** Why the node is going away. */
export const SERVER_DRAIN_REASONS = [
    /** A rolling deploy. Expected, and the common case. */
    'deploy',
    /** The node is being removed — a scale-down, a node drain, a failing host. */
    'shutdown',
    /** Sockets are being moved to spread load. Nothing is wrong. */
    'rebalance',
];
export const ServerDrainReasonSchema = z.enum(SERVER_DRAIN_REASONS).openapi('ServerDrainReason', {
    description: 'Why a realtime node is draining (spec/09 §8).',
});
export const ServerDrainingPayloadSchema = z
    .object({
    /**
     * How long this client should wait before reconnecting, on top of its own jittered backoff.
     * Spread across the node's sockets so the fleet returns over the drain window.
     */
    reconnectAfterMs: z.int().min(0).max(300_000),
    /** When this node closes the socket whatever the client does. */
    closesInMs: z.int().min(0).max(300_000),
    reason: ServerDrainReasonSchema,
})
    .openapi('ServerDrainingPayload', {
    description: 'A realtime node is draining: reconnect after the suggested delay (spec/09 §8).',
});
export const ServerDrainingMessage = realtimeMessage('evt', SERVER_DRAINING, ServerDrainingPayloadSchema, 'ServerDrainingMessage');
//# sourceMappingURL=server.js.map